import * as React from 'react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { atom, useAtomValue } from 'jotai';
import { atomFamily, useAtomCallback } from 'jotai/utils';
import { cn } from '@antimatterhq/ui/lib/utils';
import Tooltip from '@antimatterhq/ui/components/tooltip';
import { Icon, IconType } from '@antimatterhq/ui/components/icon';
import { Button } from '@antimatterhq/ui/components/button';
import { Input } from '@antimatterhq/ui/components/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@antimatterhq/ui/components/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@antimatterhq/ui/components/select';
import {
  FilterOperator,
  FilterValue,
} from '@antimatterhq/ui/types/TableFilter';
import { TableColumn } from '@antimatterhq/ui/types/TableColumn';
import { Label } from '@antimatterhq/ui/components/label';
import {
  Pagination,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationItem,
  PaginationContent,
} from '@antimatterhq/ui/components/pagination';
import { formatDate, isDateValid } from '@antimatterhq/ui/utils/format';
import { Skeleton } from '@antimatterhq/ui/components/skeleton';
import {
  SimpleTableBody,
  SimpleTableBodyProps,
  SimpleTableCaption,
  SimpleTableCell,
  SimpleTableFooter,
  SimpleTableHeader,
  SimpleTableRow,
} from '@antimatterhq/ui/components/simpleTable';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuContent,
} from '@antimatterhq/ui/components/dropdownMenu';
import { Switch } from '@antimatterhq/ui/components/switch';
import { Pre, Text } from '@antimatterhq/ui/components/typography';
import { TableColumnDefinition } from '@antimatterhq/ui/types/TableColumnDefinition';
import { compareColumnPositions } from '@antimatterhq/ui/utils/table/compareColumnPositions';
import { useStableTableKey } from '@antimatterhq/ui/hooks/table/useStableTableKey';
import { Checkbox } from '@antimatterhq/ui/components/checkbox';
import {
  TABLE_CELL_HEIGHT,
  VIRTUALIZED_TABLE_DEFAULT_PAGE_SIZE,
} from '@antimatterhq/ui/constants/table';
import Spacer from '@antimatterhq/ui/components/spacer';
import {
  FixedHeaderContent,
  ItemContent,
  TableVirtuoso,
  TableVirtuosoHandle,
  TableComponents,
  ItemProps,
  ContextProp,
  ListRange,
} from 'react-virtuoso';
import { GlobalError } from '@antimatterhq/ui/components/globalError';
import { isAxiosError } from 'axios';
import { createPortal } from 'react-dom';

// TABLE TOOLTIP ATOMS & COMPONENTS
interface TooltipState {
  isOpen: boolean;
  content: React.ReactNode;
  anchorEl: HTMLElement | null;
  anchorId: string | null;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const tooltipStateAtomFamily = atomFamily((_id: string) =>
  atom<TooltipState>({
    isOpen: false,
    content: null,
    anchorEl: null,
    anchorId: null,
  }),
);

// Centralized Tooltip Component
function TableTooltip({ tableKey }: { tableKey: string }) {
  const tooltipState = useAtomValue(tooltipStateAtomFamily(tableKey));
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null,
  );

  useEffect(() => {
    const container = document.createElement('div');
    container.className = 'table-tooltip-portal';
    document.body.appendChild(container);
    setPortalContainer(container);

    return () => {
      document.body.removeChild(container);
    };
  }, []);

  // Calculate position dynamically based on current anchor element
  const position = useMemo(() => {
    if (tooltipState.anchorEl && tooltipState.isOpen) {
      const rect = tooltipState.anchorEl.getBoundingClientRect();
      return {
        left: rect.left + rect.width / 2,
        top: rect.top,
      };
    }
    return { left: 0, top: 0 };
  }, [tooltipState.anchorEl, tooltipState.isOpen]);

  const onOpenChange = useAtomCallback(
    useCallback(
      (get, set, open: boolean) => {
        set(tooltipStateAtomFamily(tableKey), {
          ...get(tooltipStateAtomFamily(tableKey)),
          isOpen: open,
        });
      },
      [tableKey],
    ),
  );

  if (!tooltipState.isOpen || !tooltipState.anchorEl || !portalContainer) {
    return null;
  }

  return createPortal(
    <div
      className="pointer-events-none fixed z-[100]"
      style={{
        left: `${position.left}px`,
        top: `${position.top}px`,
      }}
    >
      <Tooltip
        content={tooltipState.content}
        side="top"
        open={tooltipState.isOpen}
        onOpenChange={onOpenChange}
        delayDuration={0}
        key={tooltipState.anchorId || 'tooltip'}
      >
        <div className="h-0 w-0" />
      </Tooltip>
    </div>,
    portalContainer,
  );
}

// UTILS
const isRenderableValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  )
    return true;

  if (Array.isArray(value)) {
    return value.every((item) => isRenderableValue(item));
  }

  if (React.isValidElement(value)) return true;

  return false;
};

export type SafeTableValueWrapperProps = {
  value: unknown;
  fallback?: React.ReactNode;
  'data-testid'?: string;
};

const SafeTableValueWrapper: React.FC<SafeTableValueWrapperProps> = ({
  value,
  fallback = null,
  'data-testid': dataTestId,
}) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return (
      <span
        className="line-clamp-1 w-full text-ellipsis whitespace-pre"
        data-testid={dataTestId}
      >
        {value}
      </span>
    );
  }

  if (Array.isArray(value)) {
    if (value.every((item) => isRenderableValue(item))) {
      return <span data-testid={dataTestId}>{value}</span>;
    }

    return (
      <span
        className="line-clamp-1 w-full text-ellipsis whitespace-pre"
        data-testid={dataTestId}
      >
        {fallback}
      </span>
    );
  }

  if (React.isValidElement(value)) {
    return (
      <span
        className="line-clamp-1 w-full text-ellipsis whitespace-pre"
        data-testid={dataTestId}
      >
        {value}
      </span>
    );
  }

  if (value instanceof Date && isDateValid(value.toISOString())) {
    return (
      <span data-testid={dataTestId}>{formatDate(value.toISOString())}</span>
    );
  }

  return (
    <span
      className="line-clamp-1 w-full text-ellipsis whitespace-pre"
      data-testid={dataTestId}
    >
      {fallback}
    </span>
  );
};

// EMPTY STATE

export type TableEmptyRowsProps = {
  message?: string;
  isLoading?: boolean;
  'data-testid'?: string;
  columns: TableColumn[];
  error?: Error | null;
  refetch?: () => void;
  itemHeight?: number;
};

export function TableEmptyState({
  message = 'No results found',
  'data-testid': dataTestId = 'table-empty-rows',
  isLoading = false,
  columns,
  error,
  refetch,
  itemHeight,
}: TableEmptyRowsProps) {
  const onRetryAsync = useCallback(async () => {
    if (refetch) {
      await refetch();
    }
  }, [refetch]);

  const errorMessage = useMemo(() => {
    if (!error) return undefined;
    if (isAxiosError(error)) {
      const apiMessage = (error.response?.data as { message?: string })
        ?.message;
      const parts = apiMessage?.split(':') ?? [];
      if (parts.length === 2) {
        return {
          errorTitle: parts[0],
          errorMessage: parts[1],
        };
      }
      if (apiMessage) {
        return {
          title: 'Error loading data',
          description: apiMessage,
        };
      }
      return {
        title: 'Error loading data',
        description: error.message,
      };
    }

    return {
      title: 'Error loading data',
      description: error.message,
    };
  }, [error]);

  if (errorMessage) {
    return (
      <SimpleTableBody>
        <SimpleTableRow
          hover={false}
          border={false}
          expandable={true}
          className="flex h-full w-full flex-col items-center justify-center gap-2 p-4"
        >
          <SimpleTableCell className="flex h-full w-full flex-col items-center justify-center gap-2">
            <GlobalError
              iconSize="sm"
              title={errorMessage.title}
              description={
                <Pre
                  bold
                  size="sm"
                  className="whitespace-pre text-wrap rounded-md p-4 text-red-600 dark:text-red-500"
                >
                  {errorMessage.description}
                </Pre>
              }
              onRetry={onRetryAsync}
            />
          </SimpleTableCell>
        </SimpleTableRow>
      </SimpleTableBody>
    );
  }

  if (isLoading) {
    return (
      <SimpleTableBody>
        <TableSkeletonRows
          rows={VIRTUALIZED_TABLE_DEFAULT_PAGE_SIZE}
          columns={columns}
          height={itemHeight}
        />
      </SimpleTableBody>
    );
  }

  return (
    <SimpleTableBody>
      <SimpleTableRow
        hover={false}
        border={false}
        expandable={true}
        className="flex h-full w-full flex-col items-center justify-center gap-2 p-56"
      >
        <SimpleTableCell className="flex h-full w-full flex-col items-center justify-center gap-2 p-16">
          <div>
            <Icon
              icon="lucide:list"
              className="h-16 w-16 text-gray-300 dark:text-grey-300"
              data-testid={`${dataTestId}-icon`}
            />
          </div>
          <Spacer size="base" />
          <Text color="info" data-testid={`${dataTestId}-message`}>
            {message}
          </Text>
        </SimpleTableCell>
      </SimpleTableRow>
    </SimpleTableBody>
  );
}

// BASE PROVIDER & CONTEXT

type TableContextType = {
  tableKey: string;
  columnDefinitions: TableColumnDefinition[];
};

const TableContext = React.createContext<TableContextType>({
  tableKey: '',
  columnDefinitions: [],
});

type TableContextProps = React.PropsWithChildren<{
  columnDefinitions: TableColumnDefinition[];
  localStorageKeyPrefix: string;
}>;

function TableProvider({
  columnDefinitions,
  localStorageKeyPrefix,
  children,
}: TableContextProps) {
  const tableKey = useStableTableKey(columnDefinitions, localStorageKeyPrefix);

  const contextValue = useMemo(
    () => ({ tableKey, columnDefinitions }),
    [tableKey, columnDefinitions],
  );

  return (
    <TableContext.Provider value={contextValue}>
      {!tableKey ? null : children}
      {tableKey && <TableTooltip tableKey={tableKey} />}
    </TableContext.Provider>
  );
}

export function useTableContext() {
  const context = React.useContext(TableContext);
  if (!context) {
    throw new Error('useTableContext must be used within a TableProvider');
  }
  return context as TableContextType;
}
// STYLE PROVIDER & CONTEXT

type TableStyleProviderProps = React.HTMLAttributes<HTMLDivElement> & {
  virtualized: boolean;
  itemHeight?: number;
  columnWidths: Record<string, number>;
  columnVisibility: Record<string, boolean>;
  'data-testid'?: string;
  pageSize: number | undefined;
};

const TableStyleContext = React.createContext<{
  containerWidth: number;
  totalWidth: number;
  getRowStyles: () => React.CSSProperties | undefined;
  getContainerStyles: () => React.CSSProperties | undefined;
}>({
  containerWidth: 0,
  totalWidth: 0,
  getRowStyles: () => undefined,
  getContainerStyles: () => undefined,
});

function TableStyleProvider({
  className,
  children,
  virtualized,
  itemHeight = TABLE_CELL_HEIGHT,
  columnWidths,
  columnVisibility,
  pageSize,
  'data-testid': dataTestId = 'table-style-provider',
  ...props
}: TableStyleProviderProps) {
  const { tableKey } = useTableContext();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useLayoutEffect(() => {
    const updateWidth = () => {
      if (tableContainerRef.current) {
        setContainerWidth(tableContainerRef.current.clientWidth);
      }
    };

    updateWidth();

    // Create a ResizeObserver to monitor size changes
    const resizeObserver = new ResizeObserver(updateWidth);
    if (tableContainerRef.current) {
      resizeObserver.observe(tableContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const totalWidth = useMemo(() => {
    return Object.keys(columnVisibility)
      .filter((key) => columnVisibility[key as string])
      .map((key) => columnWidths[key as string])
      .reduce((acc, width) => acc + width, 0);
  }, [columnWidths, columnVisibility]);

  const totalWidthVar = `var(--${tableKey}-total-width)`;

  // Avoid clamping on the very first render before we've measured the
  // container. A zero width means the measurement hasn't happened yet.
  const shouldClampWidth =
    containerWidth === 0 ? false : totalWidth > containerWidth;

  const getRowStyles = useCallback(() => {
    const base: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
      minHeight: TABLE_CELL_HEIGHT,
    };

    if (shouldClampWidth) {
      base.minWidth = `var(--${tableKey}-total-width)`;
    }

    return base;
  }, [tableKey, shouldClampWidth]);

  // Get table container width styles
  const getContainerStyles = useCallback(() => {
    const height = pageSize ? (pageSize + 1) * itemHeight : undefined;

    const base: React.CSSProperties = {
      width: '100%',
      display: virtualized ? 'table' : 'flex',
      flexDirection: virtualized ? undefined : 'column',
      height: height ? `${height}px` : '100%',
      minWidth: shouldClampWidth ? totalWidthVar : undefined,
    };

    return base;
  }, [pageSize, itemHeight, totalWidthVar, shouldClampWidth, virtualized]);

  const contextValue = useMemo(
    () => ({
      containerWidth,
      totalWidth,
      getRowStyles,
      getContainerStyles,
    }),
    [containerWidth, totalWidth, getRowStyles, getContainerStyles],
  );

  return (
    <TableStyleContext.Provider value={contextValue}>
      <div
        className={cn('relative w-full', virtualized ? 'h-full' : 'h-fit')}
        ref={tableContainerRef}
        data-testid={dataTestId}
        {...props}
      >
        {children}
      </div>
    </TableStyleContext.Provider>
  );
}

export function useTableStyle() {
  const context = React.useContext(TableStyleContext);
  if (!context) {
    throw new Error('useTableStyle must be used within a TableStyleProvider');
  }
  return context;
}

// TYPES

type TableProps = React.HTMLAttributes<HTMLTableElement> & {
  className?: string;
  fixed?: boolean;
  'data-testid'?: string;
};

type TableRowProps = React.HTMLAttributes<HTMLTableRowElement> & {
  className?: string;
  hover?: boolean;
  height?: number;
  border?: boolean;
  expandable?: boolean;
  'data-testid'?: string;
};

// BASE COMPONENTS

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  (
    { className, fixed = true, 'data-testid': dataTestId = 'table', ...props },
    ref,
  ) => {
    const { getContainerStyles } = useTableStyle();

    return (
      <table
        ref={ref}
        className={cn(
          'relative h-full w-full caption-bottom text-sm',
          fixed && 'table-fixed border-collapse',
          className,
        )}
        data-testid={dataTestId}
        {...props}
        style={getContainerStyles()}
      />
    );
  },
);

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement> & {
    'data-testid'?: string;
    height?: number;
  }
>(
  (
    {
      className,
      'data-testid': dataTestId = 'table-header',
      height = TABLE_CELL_HEIGHT,
      ...props
    },
    ref,
  ) => (
    <SimpleTableHeader
      ref={ref}
      className={cn(
        className,
        `sticky left-0 top-0 z-20 h-[${height}px] bg-white dark:bg-grey-800`,
      )}
      data-testid={dataTestId}
      {...props}
    />
  ),
);

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement> & { 'data-testid'?: string }
>(
  (
    { className, 'data-testid': dataTestId = 'table-footer', ...props },
    ref,
  ) => (
    <SimpleTableFooter
      ref={ref}
      className={className}
      data-testid={dataTestId}
      {...props}
    />
  ),
);

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement> & { 'data-testid'?: string }
>(
  (
    { className, 'data-testid': dataTestId = 'table-caption', ...props },
    ref,
  ) => (
    <SimpleTableCaption
      ref={ref}
      className={className}
      data-testid={dataTestId}
      {...props}
    />
  ),
);

const TableBody = ({
  children,
  'data-testid': dataTestId = 'table-body',
  ...props
}: SimpleTableBodyProps & { 'data-testid'?: string }) => {
  return (
    <SimpleTableBody {...props} data-testid={dataTestId}>
      {children}
    </SimpleTableBody>
  );
};

const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  (
    {
      className,
      expandable = false,
      hover = true,
      border = true,
      height = expandable ? undefined : TABLE_CELL_HEIGHT,
      'data-testid': dataTestId = 'table-row',
      ...props
    },
    ref,
  ) => {
    const { getRowStyles } = useTableStyle();

    const style = useMemo(() => {
      return {
        ...getRowStyles?.(),
        ...(expandable && {
          minHeight: height,
        }),
      };
    }, [getRowStyles, expandable, height]);

    return (
      <SimpleTableRow
        ref={ref}
        className={cn(className, 'flex bg-white dark:bg-grey-800')}
        border={border}
        height={height}
        expandable={expandable}
        hover={hover}
        data-testid={dataTestId}
        {...props}
        style={style}
      />
    );
  },
);

export type VirtualTableProps<T> = {
  'data-testid'?: string;
  virtuosoRef?: React.Ref<TableVirtuosoHandle>;
  fixedHeaderContent: FixedHeaderContent;
  itemContent: ItemContent<T, unknown>;
  data: T[];
  style?: React.CSSProperties;
  handleEndReached?: () => void;
  handleStartReached?: () => void;
  isLoading?: boolean;
  overscan?: number;
  emptyMessage?: string;
  columns: TableColumn[];
  error?: Error | null;
  refetch?: () => void;
  itemHeight?: number;
  onRowClick?: (row: T) => void;
  atTopThreshold?: number;
  atBottomThreshold?: number;
  firstItemIndex?: number;
  handleRangeChanged?: (range: ListRange) => void;
};

const MemoTableRow = React.memo(TableRow);

function createVirtualTableRow<T>(
  itemHeight?: number,
  onRowClick?: (row: T) => void,
) {
  const VirtualTableRow = React.forwardRef<
    HTMLTableRowElement,
    ItemProps<T> & ContextProp<unknown>
  >((props, ref) => {
    const { item, context, ...restProps } = props;

    const onClick = useCallback(() => {
      if (onRowClick) {
        onRowClick(item);
      }
    }, [item]);

    return (
      <MemoTableRow
        ref={ref}
        {...restProps}
        height={itemHeight}
        onClick={onClick}
      />
    );
  });
  VirtualTableRow.displayName = 'VirtualTableRow';
  return VirtualTableRow;
}

function VirtualTable<T>({
  'data-testid': dataTestId = 'table-body',
  virtuosoRef,
  fixedHeaderContent,
  itemContent,
  data,
  style,
  handleEndReached,
  handleStartReached,
  isLoading,
  emptyMessage,
  overscan = 5,
  columns,
  error,
  refetch,
  itemHeight,
  onRowClick,
  atTopThreshold = 0,
  atBottomThreshold = 0,
  firstItemIndex,
  handleRangeChanged,
}: VirtualTableProps<T>) {
  const EmptyPlaceholder = useCallback(
    () => (
      <TableEmptyState
        error={error}
        refetch={refetch}
        message={emptyMessage}
        isLoading={isLoading}
        columns={columns}
        itemHeight={itemHeight}
        data-testid={`${dataTestId}-empty-state`}
      />
    ),
    [error, refetch, emptyMessage, isLoading, columns, itemHeight, dataTestId],
  );

  const components: TableComponents<T, unknown> = useMemo(
    () => ({
      Table,
      TableHead: TableHeader,
      TableRow: createVirtualTableRow(itemHeight, onRowClick),
      EmptyPlaceholder,
    }),
    [EmptyPlaceholder, itemHeight, onRowClick],
  );

  return (
    <>
      <TableVirtuoso<T, unknown>
        ref={virtuosoRef}
        style={{ height: '100%', ...style }}
        data={data}
        fixedHeaderContent={fixedHeaderContent}
        itemContent={itemContent}
        totalCount={data.length}
        data-testid={dataTestId}
        overscan={overscan}
        endReached={handleEndReached}
        startReached={handleStartReached}
        atTopThreshold={atTopThreshold}
        atBottomThreshold={atBottomThreshold}
        components={components}
        firstItemIndex={firstItemIndex}
        rangeChanged={handleRangeChanged}
        className="custom-scrollbar"
      />
      {isLoading && (
        <div className="absolute bottom-4 right-4 z-10 flex h-8 w-32 items-center justify-center gap-2 rounded-md bg-transparent shadow-md backdrop-blur-sm">
          <Text>Loading...</Text>
          <Icon
            icon="lucide:loader-circle"
            className={cn(
              'h-6 w-6 animate-spin text-gray-800 dark:text-gray-300',
            )}
          />
        </div>
      )}
    </>
  );
}

// CUSTOM COMPONENTS

export type TableHeaderCellFilterProps = {
  columnName: React.ReactNode;
  filterValue: FilterValue | undefined;
  filterOperator?: FilterOperator;
  setFilter?: (value: FilterValue, operator?: FilterOperator) => void;
  className?: string;
  onOpenChange?: (open: boolean) => void;
  'data-testid'?: string;
};

export const FILTER_OPERATORS: { value: FilterOperator; label: string }[] = [
  { value: 'contains', label: 'Contains' },
  { value: 'equals', label: 'Equals' },
  { value: 'startsWith', label: 'Starts with' },
  { value: 'endsWith', label: 'Ends with' },
  { value: 'gt', label: 'Greater than' },
  { value: 'gte', label: 'Greater than or equal' },
  { value: 'lt', label: 'Less than' },
  { value: 'lte', label: 'Less than or equal' },
];

function TableHeaderCellFilter({
  columnName,
  filterValue,
  filterOperator = 'contains',
  setFilter,
  onOpenChange,
  'data-testid': dataTestId = 'table-header-cell-filter',
  className,
}: TableHeaderCellFilterProps) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<string>(
    filterValue ? String(filterValue) : '',
  );
  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  const [operator, setOperator] = useState<FilterOperator>(filterOperator);

  const hasActiveFilter = !!filterValue;

  const handleApplyFilter = () => {
    setFilter?.(value, operator);
    setOpen(false);
  };

  const handleClearFilter = () => {
    setValue('');
    setFilter?.(null, operator);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Icon
          icon="antimatterhq:filter"
          className={cn(
            'h-4 w-4 cursor-pointer opacity-50 hover:text-blue-500 hover:opacity-100',
            hasActiveFilter && 'text-blue-600 opacity-100',
            className,
          )}
          data-testid={`${dataTestId}-icon`}
        />
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-3"
        align="start"
        data-testid={`${dataTestId}-content`}
      >
        <div className="space-y-4">
          <div
            className="text-sm font-medium"
            data-testid={`${dataTestId}-title`}
          >
            Filter {columnName}
          </div>

          <Select
            value={operator}
            onValueChange={(op) => setOperator(op as FilterOperator)}
            data-testid={`${dataTestId}-operator-select`}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a filter type" />
            </SelectTrigger>
            <SelectContent>
              {FILTER_OPERATORS.map((op) => (
                <SelectItem
                  key={op.value}
                  value={op.value}
                  data-testid={`${dataTestId}-operator-option-${op.value}`}
                >
                  {op.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            placeholder={'Filter value...'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="w-full"
            data-testid={`${dataTestId}-value-input`}
          />

          <div className="flex justify-between">
            <Button
              variant="link"
              onClick={handleClearFilter}
              data-testid={`${dataTestId}-clear-btn`}
            >
              Clear
            </Button>
            <Button
              variant="primary"
              className="ml-auto"
              onClick={handleApplyFilter}
              data-testid={`${dataTestId}-apply-btn`}
            >
              Apply filter
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export type TableCellProps = React.TdHTMLAttributes<HTMLTableCellElement> & {
  column: TableColumn;
  className?: string;
  truncate?: boolean;
  hover?: boolean;
  'data-testid'?: string;
};

function TableCell({
  column,
  hover = true,
  truncate = true,
  className,
  children,
  'data-testid': dataTestId = `table-cell-${column.id}`,
  ...props
}: TableCellProps) {
  const { tableKey } = useTableContext();
  const getCellWidth = column.getCellWidth;
  const getCellStyles = column.getCellStyles;
  const cellRef = useRef<HTMLTableCellElement>(null);

  const style = useMemo(() => getCellStyles?.(), [getCellStyles]);
  const width = useMemo(() => getCellWidth(), [getCellWidth]);
  const tooltip = column.tooltip;

  const showTooltip = useAtomCallback(
    useCallback(
      (_get, set) => {
        if (tooltip && cellRef.current) {
          // Use a stable ID based on column and row position
          const cellId =
            cellRef.current.getAttribute('data-testid') ||
            `cell-${column.id}-${Math.random().toString(36).substr(2, 9)}`;
          set(tooltipStateAtomFamily(tableKey), {
            isOpen: true,
            content: children,
            anchorEl: cellRef.current,
            anchorId: cellId,
          });
        }
      },
      [tooltip, column.id, tableKey, children],
    ),
  );

  const hideTooltip = useAtomCallback(
    useCallback(
      (_get, set) => {
        if (tooltip) {
          set(tooltipStateAtomFamily(tableKey), {
            isOpen: false,
            content: null,
            anchorEl: null,
            anchorId: null,
          });
        }
      },
      [tableKey, tooltip],
    ),
  );

  return (
    <td
      ref={cellRef}
      className={cn(
        'flex items-center',
        'px-2 py-1.5 align-middle text-grey-800 dark:text-grey-100',
        'bg-white dark:bg-grey-800',
        '[&:has([role=checkbox])]:pr-0',
        `w-[${width}px]`,
        'text-ellipsis whitespace-pre',
        hover && 'group-hover:bg-gray-100 dark:group-hover:bg-gray-800',
        truncate && 'truncate',
        className,
      )}
      style={style}
      data-testid={dataTestId}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      {...props}
    >
      {children}
    </td>
  );
}

export type TableHeadProps = React.ThHTMLAttributes<HTMLTableCellElement> & {
  column: TableColumn;
  children: React.ReactNode;
  className?: string;
  tooltip?: boolean;
  bold?: boolean;
  hover?: boolean;
  'data-testid'?: string;
};

function TableHead({
  column,
  children,
  className,
  bold = true,
  hover = true,
  'data-testid': dataTestId = `table-head-${column.id}`,
  ...props
}: TableHeadProps) {
  const ref = useRef<HTMLTableCellElement>(null);

  const tooltip = column.tooltip;
  const handleResize = column.handleResize;
  const setSortBy = column.setSortBy;
  const setFilter = column.setFilter;
  const getCellStyles = column.getCellStyles;

  const style = useMemo(() => getCellStyles?.(), [getCellStyles]);

  const width = column.getCellWidth();

  const [isResizing, setIsResizing] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(width);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      setStartX(e.clientX);
      setStartWidth(width);
    },
    [width],
  );

  React.useLayoutEffect(() => {
    if (width) {
      handleResize?.(width);
    } else {
      // get the width of the column
      const columnWidth = ref.current?.getBoundingClientRect().width;
      if (columnWidth) handleResize?.(columnWidth);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;

      const diff = e.clientX - startX;
      const newWidth = (startWidth ?? 0) + diff;
      handleResize?.(newWidth);
    },
    [isResizing, startX, startWidth, handleResize],
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    column.finalizeResize?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [column.finalizeResize]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const isChildString = typeof children === 'string';

  const calculatedMaxWidth = useMemo(() => {
    let offset = 0;
    if (setSortBy) offset += 16;
    if (setFilter) offset += 16 + 8;
    if (handleResize) offset += 16 + 8;
    return width - offset;
  }, [width, setSortBy, setFilter, handleResize]);

  const content = (
    <span className="group flex h-full w-full items-center gap-1 pr-2">
      {tooltip ? (
        <Tooltip
          style={{ maxWidth: calculatedMaxWidth }}
          content={children}
          side="top"
          className="flex items-center"
        >
          <span className={cn(isChildString && 'line-clamp-1 block truncate')}>
            {children}
          </span>
        </Tooltip>
      ) : (
        <span style={{ maxWidth: calculatedMaxWidth }}>
          <span className={cn(isChildString && 'line-clamp-1 block truncate')}>
            {children}
          </span>
        </span>
      )}
      {setFilter && (
        <TableHeaderCellFilter
          columnName={column.title}
          filterValue={column.filterValue}
          filterOperator={column.filterOperator}
          setFilter={setFilter}
          data-testid={`${dataTestId}-filter`}
          className={cn(
            'absolute right-5 top-1/2 !h-4 !w-4 -translate-y-1/2',
            setSortBy && 'right-10',
          )}
        />
      )}
      {setSortBy && (
        <Icon
          onClick={setSortBy}
          icon={
            column.sortDirection === 'asc'
              ? 'antimatterhq:sort-ascending'
              : 'antimatterhq:sort-descending'
          }
          className="absolute right-5 top-1/2 !h-4 !w-4 -translate-y-1/2 cursor-pointer opacity-50 hover:text-blue-500 hover:opacity-100"
        />
      )}

      {handleResize && (
        <span
          className="absolute right-0 top-1/2 -translate-y-1/2 cursor-col-resize opacity-25 hover:opacity-100"
          onMouseDown={handleMouseDown}
        >
          <Icon icon="radix-icons:drag-handle-dots-2" className="h-4 w-4" />
        </span>
      )}
    </span>
  );

  return (
    <th
      ref={ref}
      className={cn(
        'relative',
        'flex items-center',
        'h-8 px-2 text-left align-middle font-medium text-grey-800 dark:text-grey-100',
        'bg-white dark:bg-grey-800',
        '[&:has([role=checkbox])]:pr-0',
        bold && 'bold',
        hover && 'group-hover:bg-gray-100 dark:group-hover:bg-gray-800',
        className,
      )}
      style={style}
      data-testid={dataTestId}
      {...props}
    >
      {content}
    </th>
  );
}

// PAGE SIZE SELECTOR

export type TablePageSizeSelectorProps = {
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  options?: number[];
  'data-testid'?: string;
};

function TablePageSizeSelector({
  pageSize,
  onPageSizeChange,
  options = [10, 20, 50, 100],
  'data-testid': dataTestId = 'table-page-size-selector',
}: TablePageSizeSelectorProps) {
  return (
    <div
      className="flex items-center justify-center gap-1"
      data-testid={dataTestId}
    >
      <Label className="min-w-28" mb={0} data-testid={`${dataTestId}-label`}>
        Items per page
      </Label>
      <Select
        value={String(pageSize)}
        onValueChange={(value) => onPageSizeChange(Number(value))}
        data-testid={`${dataTestId}-select`}
      >
        <SelectTrigger className="h-8 w-[70px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((size) => (
            <SelectItem
              key={size}
              value={String(size)}
              data-testid={`${dataTestId}-option-${size}`}
            >
              {size}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// PAGINATION

export type TablePaginationProps = {
  currentPage: number;
  totalPages?: number;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onPageChange: (page: number) => void;
  nextPageEnabled: boolean;
  previousPageEnabled: boolean;
  simple?: boolean;
  hasLabel?: boolean;
  showPageCount?: boolean;
  className?: string;
  'data-testid'?: string;
};

function TablePagination({
  currentPage,
  totalPages,
  onPreviousPage,
  onNextPage,
  onPageChange,
  nextPageEnabled,
  previousPageEnabled,
  hasLabel = true,
  showPageCount = true,
  className,
  'data-testid': dataTestId = 'table-pagination',
}: TablePaginationProps) {
  return (
    <Pagination
      className={cn('justify-end p-2', className)}
      data-testid={dataTestId}
    >
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            onClick={onPreviousPage}
            className="cursor-pointer"
            disabled={!previousPageEnabled}
            hasLabel={hasLabel}
            data-testid={`${dataTestId}-previous`}
          />
        </PaginationItem>
        {showPageCount &&
          totalPages &&
          totalPages > 1 &&
          Array.from({ length: totalPages }).map((_, index) => (
            <PaginationItem key={index}>
              <PaginationLink
                isActive={currentPage === index}
                onClick={() => onPageChange(index)}
                aria-label={`Page ${index}`}
                data-testid={`${dataTestId}-page-${index}`}
              >
                {index + 1}
              </PaginationLink>
            </PaginationItem>
          ))}
        <PaginationItem>
          <PaginationNext
            onClick={onNextPage}
            className="cursor-pointer"
            disabled={!nextPageEnabled}
            hasLabel={hasLabel}
            data-testid={`${dataTestId}-next`}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

// SKELETON ROWS

function TableSkeletonRows({
  rows = 10,
  columns,
  height = TABLE_CELL_HEIGHT,
  'data-testid': dataTestId = 'table-skeleton-rows',
}: {
  rows?: number;
  columns: TableColumn[];
  height?: number;
  getRowStyles?: () => React.CSSProperties | undefined;
  'data-testid'?: string;
}) {
  const { getRowStyles } = useTableStyle();

  return (
    <>
      {Array.from({ length: rows }).map((_, index) => (
        <TableRow
          key={`skeleton-${index}`}
          height={height}
          style={getRowStyles?.()}
          border={false}
          hover={false}
          data-testid={`${dataTestId}-row-${index}`}
        >
          {columns.map((column) => (
            <TableCell
              key={column.id}
              column={{ ...column, tooltip: false }}
              width={column.getCellWidth()}
              hover={false}
              style={column.getCellStyles?.()}
              data-testid={`${dataTestId}-cell-${column.id}-${index}`}
            >
              <Skeleton
                style={{
                  height: `${TABLE_CELL_HEIGHT / 2}px`,
                  width: '100%',
                  display: 'flex',
                }}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

// COLUMN MANAGER

type TableColumnManagerProps = {
  columnVisibility: Record<string, boolean>;
  columnOrders: Record<string, number>;
  pinnedColumns: Record<string, 'left' | 'right' | undefined>;
  toggleColumnVisibility: (columnId: string) => void;
  className?: string;
  resetColumns: () => void;
  onColumnOrderChange: (id: string, oldIndex: number, newIndex: number) => void;
  'data-testid'?: string;
};

function TableColumnManagerSortableColumnItem({
  title,
  id,
  visible,
  toggleColumnVisibility,
  disabled,
  'data-testid': dataTestId = `column-manager-item-${id}`,
}: {
  title: string | (() => React.ReactNode);
  id: string;
  visible: boolean;
  toggleColumnVisibility: (columnId: string) => void;
  disabled: boolean;
  'data-testid'?: string;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(id) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleColumnVisibilityChange = useCallback(() => {
    toggleColumnVisibility(id as string);
  }, [toggleColumnVisibility, id]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex flex-row items-center justify-between gap-2 rounded-sm px-2 py-1.5',
        isDragging ? 'bg-gray-100' : '',
      )}
      data-testid={dataTestId}
    >
      <div className="flex flex-row items-center gap-2">
        <button
          className={cn(
            'cursor-grab touch-none p-1 opacity-70 hover:opacity-100',
            !visible && 'cursor-not-allowed opacity-50 hover:opacity-50',
          )}
          {...attributes}
          {...(visible ? listeners : {})}
          data-testid={`${dataTestId}-drag-handle`}
        >
          <Icon
            icon="lucide:grip-vertical"
            className="h-4 w-4 dark:text-white"
          />
        </button>
        <Text
          className={cn(
            'cursor-pointer',
            !visible && 'opacity-50 hover:opacity-50',
          )}
          onClick={handleColumnVisibilityChange}
          data-testid={`${dataTestId}-title`}
        >
          {typeof title === 'function' ? title() : title}
        </Text>
      </div>
      <Switch
        checked={visible}
        onCheckedChange={handleColumnVisibilityChange}
        disabled={disabled}
        data-testid={`${dataTestId}-visibility-switch`}
      />
    </div>
  );
}

function TableColumnManager({
  className,
  columnOrders,
  pinnedColumns,
  toggleColumnVisibility,
  resetColumns,
  onColumnOrderChange,
  columnVisibility,
  'data-testid': dataTestId = 'table-column-manager',
}: TableColumnManagerProps) {
  const { columnDefinitions } = useTableContext();
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Minimum drag distance before activation
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const tableDefinitionIdToTitle = useMemo(() => {
    return columnDefinitions.reduce(
      (acc, col) => {
        return { ...acc, [col.id]: col.title };
      },
      {} as Record<string, string | (() => React.ReactNode)>,
    );
  }, [columnDefinitions]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) return;

      // Get the sorted list using your comparison function
      const sortedColumnIds = Object.keys(columnOrders)
        .map((id) => ({ id }))
        .sort((a, b) =>
          compareColumnPositions(a, b, pinnedColumns, columnOrders),
        )
        .map((item) => item.id);

      // Find the indices in the properly sorted array
      const oldIndex = sortedColumnIds.indexOf(String(active.id));
      const newIndex = sortedColumnIds.indexOf(String(over.id));

      if (oldIndex === -1 || newIndex === -1) return;

      onColumnOrderChange(active.id as string, oldIndex, newIndex);
    },
    [onColumnOrderChange, columnOrders, pinnedColumns],
  );

  const sortedColumns = useMemo(
    () =>
      Object.keys(columnOrders).sort(
        (a, b) => columnOrders[a as string] - columnOrders[b as string],
      ),
    [columnOrders],
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn('flex h-4 w-4 items-center gap-2', className)}
          data-testid={`${dataTestId}`}
        >
          <Icon icon="antimatterhq:columns" className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-64"
        data-testid={`${dataTestId}-content`}
      >
        <Text
          semiBold
          className="px-3 py-2"
          data-testid={`${dataTestId}-title`}
        >
          Manage columns
        </Text>
        <div className="my-1 h-px bg-gray-200"></div>
        <div className="max-h-[calc(100vh*0.5)] overflow-y-auto custom-scrollbar">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortedColumns}
              strategy={verticalListSortingStrategy}
            >
              {sortedColumns.map((columnId) => (
                <TableColumnManagerSortableColumnItem
                  key={columnId}
                  title={tableDefinitionIdToTitle[columnId]}
                  id={columnId}
                  disabled={
                    Object.keys(columnVisibility).filter(
                      (value) =>
                        !pinnedColumns[value as string] &&
                        !!columnVisibility[value as string],
                    ).length === 1 &&
                    columnVisibility[columnId as string] === true
                  }
                  visible={columnVisibility[columnId as string]}
                  toggleColumnVisibility={toggleColumnVisibility}
                  data-testid={`${dataTestId}-item-${columnId}`}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
        <div className="my-2 h-px bg-gray-200"></div>
        <Button
          className="w-full"
          onClick={resetColumns}
          data-testid={`${dataTestId}-reset-button`}
        >
          <Text semiBold>Reset columns</Text>
        </Button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// SELECT COLUMN

const TableSelectCell = ({
  selected,
  id,
  onToggleSelect,
  disabled,
  'data-testid': dataTestId = `table-select-cell-${id}`,
}: {
  selected: boolean;
  id: string;
  onToggleSelect: (id: string, checked: boolean) => void;
  disabled?: boolean;
  'data-testid'?: string;
}) => {
  const handleCheckedChange = useCallback(
    (checked: boolean) => onToggleSelect(id, checked === true),
    [onToggleSelect, id],
  );

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <div
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      className="flex items-center justify-center"
    >
      <Checkbox
        checked={selected}
        onCheckedChange={handleCheckedChange}
        disabled={disabled}
        className="m-auto p-0"
        data-testid={dataTestId}
      />
    </div>
  );
};

const TableSelectHeaderCell = ({
  isAllSelected,
  onToggleSelectAll,
  disabled,
  'data-testid': dataTestId = 'table-select-header-cell',
}: {
  isAllSelected: boolean;
  onToggleSelectAll: (checked: boolean) => void;
  disabled?: boolean;
  'data-testid'?: string;
}) => {
  return (
    <Checkbox
      checked={isAllSelected}
      onCheckedChange={onToggleSelectAll}
      disabled={disabled}
      data-testid={dataTestId}
    />
  );
};

// ACTION CELL

export type TableActionsCellAction = {
  label: string;
  icon: IconType;
  onClick?: () => void | Promise<void>;
  subMenuItems?: {
    label: string;
    onClick: () => void | Promise<void>;
    'data-testid'?: string;
  }[];
  'data-testid'?: string;
};

export type TableActionsCellProps = {
  disabled?: boolean;
  'data-testid'?: string;
  actions: TableActionsCellAction[];
};

const TableActionsCell = ({
  disabled,
  actions,
  'data-testid': dataTestId = 'actions-cell',
}: TableActionsCellProps) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="small"
          leftIcon={'radix-icons:dots-horizontal'}
          className={'h-5 p-0'}
          disabled={disabled}
          data-testid={dataTestId}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" data-testid={dataTestId}>
        {actions.map((action) => {
          if (action.subMenuItems) {
            return (
              <DropdownMenuSub key={action.label}>
                <DropdownMenuSubTrigger data-testid={action['data-testid']}>
                  <Text className="flex w-full items-center justify-start bg-inherit">
                    <Icon icon={action.icon} className="mr-2 h-4 w-4" />
                    {action.label}
                  </Text>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="bg-white dark:bg-[#11171C]">
                  {action.subMenuItems.map((subItem) => (
                    <DropdownMenuItem
                      key={subItem.label}
                      onClick={subItem.onClick}
                      data-testid={subItem['data-testid']}
                    >
                      {subItem.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            );
          }
          return (
            <DropdownMenuItem
              key={action.label}
              onClick={action.onClick}
              data-testid={action['data-testid']}
            >
              <Text className="flex items-center justify-start bg-inherit">
                <Icon icon={action.icon} className="mr-2 h-4 w-4" />
                {action.label}
              </Text>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// DISPLAY NAMES

TableStyleProvider.displayName = 'TableStyleProvider';
Table.displayName = 'Table';
TableHeader.displayName = 'TableHeader';
TableBody.displayName = 'TableBody';
TableFooter.displayName = 'TableFooter';
TableRow.displayName = 'TableRow';
TableCaption.displayName = 'TableCaption';
SafeTableValueWrapper.displayName = 'SafeTableValueWrapper';
TableHead.displayName = 'TableHead';
TableCell.displayName = 'TableCell';
TablePageSizeSelector.displayName = 'TablePageSizeSelector';
TablePagination.displayName = 'TablePagination';
TableSkeletonRows.displayName = 'TableSkeletonRows';
TableColumnManager.displayName = 'TableColumnManager';

export type VirtualizedPageContainerProps = {
  children: React.ReactNode;
  className?: string;
  'data-testid'?: string;
};

/**
 * This component is used to wrap the table in a container that is used to display the table.
 * It is used to size the table to the height of the page minus the header.
 */
function VirtualizedTablePageContainer({
  children,
  className,
  'data-testid': dataTestId = 'virtualized-page-container',
}: VirtualizedPageContainerProps) {
  return (
    <div
      className={cn('h-[calc(100vh-176px)] w-full', className)}
      data-testid={dataTestId}
    >
      {children}
    </div>
  );
}

export type ResponsiveTableContainerProps = {
  children: React.ReactNode;
  className?: string;
  containerClassName?: string;
  'data-testid'?: string;
};

function ResponsiveTableContainer({
  children,
  className,
  containerClassName,
  'data-testid': dataTestId = 'responsive-table-container',
}: ResponsiveTableContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const [containerWidth, setContainerWidth] = useState<number | undefined>();

  useEffect(() => {
    // Initial width
    setContainerWidth(containerRef.current?.clientWidth);
    // Create ResizeObserver to watch for size changes
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    // Cleanup
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className={cn('h-full w-full', containerClassName)}
      data-testid={dataTestId}
    >
      <div
        className={cn('relative h-full w-full overflow-y-auto', className)}
        style={{ width: containerWidth }}
      >
        {children}
      </div>
    </div>
  );
}

export default {
  Table,
  Header: TableHeader,
  Body: TableBody,
  Virtual: VirtualTable,
  Footer: TableFooter,
  Row: React.memo(TableRow),
  Caption: TableCaption,
  Provider: TableProvider,
  StyleProvider: TableStyleProvider,
  SafeValueWrapper: SafeTableValueWrapper,
  Head: React.memo(TableHead),
  Cell: React.memo(TableCell),
  PageSizeSelector: TablePageSizeSelector,
  Pagination: TablePagination,
  SkeletonRows: TableSkeletonRows,
  ColumnManager: TableColumnManager,
  SelectCell: TableSelectCell,
  SelectHeaderCell: TableSelectHeaderCell,
  EmptyState: TableEmptyState,
  ActionsCell: TableActionsCell,
  VirtualizedPageContainer: VirtualizedTablePageContainer,
  ResponsiveContainer: ResponsiveTableContainer,
};
